import React, { useState, useEffect } from 'react';
import { Shop, ShopProduct } from '../services/shopService';
import { getValidImageUrl } from '../utils/imageUtils';
import './CrossShopCart.css';

interface CartItem {
  product: ShopProduct;
  shop: Shop;
  quantity: number;
}

interface CrossShopCartProps {
  items: CartItem[];
  savedItems?: CartItem[];
  onUpdateQuantity: (productId: string, shopId: string, quantity: number) => void;
  onRemoveItem: (productId: string, shopId: string) => void;
  onSaveForLater?: (productId: string, shopId: string) => void;
  onMoveToCart?: (productId: string, shopId: string) => void;
  onRemoveFromSaved?: (productId: string, shopId: string) => void;
  onContinueShopping?: () => void;
  onCheckout: (shopOrders: { shop: Shop; items: CartItem[] }[]) => void;
}

const CrossShopCart: React.FC<CrossShopCartProps> = ({
  items,
  savedItems = [],
  onUpdateQuantity,
  onRemoveItem,
  onSaveForLater,
  onMoveToCart,
  onRemoveFromSaved,
  onContinueShopping,
  onCheckout
}) => {
  const [groupedItems, setGroupedItems] = useState<{ [shopId: string]: { shop: Shop; items: CartItem[] } }>({});
  const [groupedSavedItems, setGroupedSavedItems] = useState<{ [shopId: string]: { shop: Shop; items: CartItem[] } }>({});

  // Group items by shop
  useEffect(() => {
    console.log('🛒 CrossShopCart: Grouping items, received items:', items);
    console.log('🛒 CrossShopCart: Items count:', items.length);
    
    const grouped: { [shopId: string]: { shop: Shop; items: CartItem[] } } = {};
    
    items.forEach((item, index) => {
      if (!item.shop || !item.shop.id) {
        console.warn(`⚠️ CrossShopCart: Item ${index} missing shop:`, item);
        return;
      }
      if (!item.product) {
        console.warn(`⚠️ CrossShopCart: Item ${index} missing product:`, item);
        return;
      }
      
      if (!grouped[item.shop.id]) {
        grouped[item.shop.id] = {
          shop: item.shop,
          items: []
        };
      }
      grouped[item.shop.id].items.push(item);
      console.log(`🛒 CrossShopCart: Added item ${index} to shop ${item.shop.name}:`, item.product.name);
    });

    console.log('🛒 CrossShopCart: Grouped items:', grouped);
    console.log('🛒 CrossShopCart: Number of shops:', Object.keys(grouped).length);
    setGroupedItems(grouped);
  }, [items]);

  // Group saved items by shop
  useEffect(() => {
    const grouped: { [shopId: string]: { shop: Shop; items: CartItem[] } } = {};
    
    savedItems.forEach(item => {
      if (!grouped[item.shop.id]) {
        grouped[item.shop.id] = {
          shop: item.shop,
          items: []
        };
      }
      grouped[item.shop.id].items.push(item);
    });

    setGroupedSavedItems(grouped);
  }, [savedItems]);

  // Calculate totals
  const calculateShopTotal = (shopItems: CartItem[]) => {
    return shopItems.reduce((total, item) => total + (item.product.price * item.quantity), 0);
  };

  const calculateGrandTotal = () => {
    return Object.values(groupedItems).reduce((total, shopGroup) => {
      return total + calculateShopTotal(shopGroup.items);
    }, 0);
  };

  const getTotalItems = () => {
    return items.reduce((total, item) => total + item.quantity, 0);
  };

  const handleQuantityChange = (productId: string, shopId: string, newQuantity: number) => {
    if (newQuantity <= 0) {
      onRemoveItem(productId, shopId);
    } else {
      onUpdateQuantity(productId, shopId, newQuantity);
    }
  };

  const handleCheckout = () => {
    console.log('Checkout button clicked!');
    console.log('Current items:', items);
    console.log('Items stock status:', items.map(item => ({ 
      name: item.product.name, 
      inStock: item.product.inStock 
    })));
    console.log('Grouped items:', groupedItems);
    const shopOrders = Object.values(groupedItems).map(shopGroup => ({
      shop: shopGroup.shop,
      items: shopGroup.items
    }));
    console.log('Shop orders to be processed:', shopOrders);
    onCheckout(shopOrders);
  };

  // Debug logging
  useEffect(() => {
    console.log('🛒 CrossShopCart: Component rendered');
    console.log('🛒 CrossShopCart: Items prop:', items);
    console.log('🛒 CrossShopCart: Items length:', items.length);
    console.log('🛒 CrossShopCart: Grouped items:', groupedItems);
    console.log('🛒 CrossShopCart: Number of shops:', Object.keys(groupedItems).length);
  }, [items, groupedItems]);

  // Check if localStorage has items but state doesn't
  useEffect(() => {
    if (items.length === 0) {
      try {
        const storedCart = localStorage.getItem('multiShopCart');
        if (storedCart) {
          const parsedCart = JSON.parse(storedCart);
          if (Array.isArray(parsedCart) && parsedCart.length > 0) {
            // Check if it's nested format
            const firstItem = parsedCart[0];
            let hasItems = false;
            
            if (firstItem?.shop && firstItem?.items && Array.isArray(firstItem.items)) {
              // Nested format
              const totalItems = parsedCart.reduce((sum: number, shopCart: any) => {
                return sum + (shopCart.items?.length || 0);
              }, 0);
              hasItems = totalItems > 0;
              console.warn('⚠️ CrossShopCart: localStorage has nested cart with', totalItems, 'items but state is empty!');
            } else if (firstItem?.product || firstItem?.quantity !== undefined) {
              // Flat format
              hasItems = parsedCart.length > 0;
              console.warn('⚠️ CrossShopCart: localStorage has flat cart with', parsedCart.length, 'items but state is empty!');
            }
            
            if (hasItems) {
              console.log('🛒 CrossShopCart: Triggering cart reload due to mismatch');
              // Trigger multiple reload attempts
              window.dispatchEvent(new CustomEvent('cartUpdated'));
              setTimeout(() => window.dispatchEvent(new CustomEvent('cartUpdated')), 100);
              setTimeout(() => window.dispatchEvent(new CustomEvent('cartUpdated')), 300);
            }
          }
        }
      } catch (error) {
        console.error('❌ CrossShopCart: Error checking localStorage:', error);
      }
    }
  }, [items.length]);

  if (items.length === 0) {
    console.log('🛒 CrossShopCart: Showing empty cart message');
    return (
      <div className="empty-cart">
        <div className="empty-cart-icon">🛒</div>
        <h3>Your cart is empty</h3>
        <p>Add some products from our shops to get started!</p>
        <button 
          onClick={() => {
            console.log('🛒 CrossShopCart: Manual reload requested');
            // Trigger multiple reload attempts
            window.dispatchEvent(new CustomEvent('cartUpdated'));
            setTimeout(() => window.dispatchEvent(new CustomEvent('cartUpdated')), 100);
            setTimeout(() => window.dispatchEvent(new CustomEvent('cartUpdated')), 300);
          }}
          style={{ marginTop: '1rem', padding: '0.5rem 1rem', cursor: 'pointer' }}
        >
          Refresh Cart
        </button>
      </div>
    );
  }

  return (
    <div className="cross-shop-cart">
      <div className="cart-header">
        <h2>🛒 Shopping Cart</h2>
        <div className="cart-summary">
          <span className="item-count">{getTotalItems()} items</span>
          <span className="grand-total">Total: ${calculateGrandTotal().toFixed(2)}</span>
        </div>
      </div>

      <div className="cart-content">
        {Object.values(groupedItems).map(shopGroup => (
          <div key={shopGroup.shop.id} className="shop-cart-section">
            <div className="shop-header">
              <div className="shop-info">
                <h3>
                  {shopGroup.shop.type === 'medical' ? '🏥' : 
                   shopGroup.shop.type === 'bakery' ? '🥖' : 
                   shopGroup.shop.type === 'restaurant' ? '🍛' : '🏪'} {shopGroup.shop.name}
                </h3>
                <p>{shopGroup.shop.description}</p>
                <span className="shop-type-badge">{shopGroup.shop.type}</span>
              </div>
              <div className="shop-total">
                <span className="shop-total-label">Shop Total:</span>
                <span className="shop-total-amount">
                  ${calculateShopTotal(shopGroup.items).toFixed(2)}
                </span>
              </div>
            </div>

            <div className="shop-items">
              {shopGroup.items.map(item => (
                <div key={`${item.product.id}-${item.shop.id}`} className="cart-item">
                  <div className="item-image">
                    <img 
                      src={getValidImageUrl(item.product.image)}
                      alt={item.product.name}
                      onError={(e) => {
                        console.log('🌐 CrossShopCart - Image failed to load:', item.product.image);
                        e.currentTarget.src = 'https://via.placeholder.com/80x80/667eea/ffffff?text=No+Image';
                      }}
                      onLoad={() => console.log('🌐 CrossShopCart - Image loaded successfully:', item.product.image)}
                    />
                  </div>
                  
                  <div className="item-details">
                    <h4>{item.product.name}</h4>
                    <p className="item-brand">{item.product.brand}</p>
                    <p className="item-description">{item.product.description}</p>
                    <div className="item-price">
                      ${item.product.price.toFixed(2)} each
                    </div>
                  </div>

                  <div className="item-quantity">
                    <label>Qty:</label>
                    <div className="quantity-controls">
                      <button
                        onClick={() => handleQuantityChange(item.product.id, item.shop.id, item.quantity - 1)}
                        className="quantity-btn"
                        disabled={item.quantity <= 1}
                      >
                        -
                      </button>
                      <span className="quantity-value">{item.quantity}</span>
                      <button
                        onClick={() => handleQuantityChange(item.product.id, item.shop.id, item.quantity + 1)}
                        className="quantity-btn"
                        disabled={!item.product.inStock}
                      >
                        +
                      </button>
                    </div>
                    {!item.product.inStock && (
                      <span className="out-of-stock-warning">Out of Stock</span>
                    )}
                  </div>

                  <div className="item-total">
                    <span className="item-total-amount">
                      ${(item.product.price * item.quantity).toFixed(2)}
                    </span>
                  </div>

                  <div className="item-actions">
                    <button
                      onClick={() => {
                        if (window.confirm(`Remove "${item.product.name}" from cart?`)) {
                          onRemoveItem(item.product.id, item.shop.id);
                        }
                      }}
                      className="remove-btn"
                      title="Delete item from cart"
                      aria-label="Delete item"
                    >
                      🗑️ Delete
                    </button>
                    {onSaveForLater && (
                      <button
                        onClick={() => onSaveForLater(item.product.id, item.shop.id)}
                        className="save-for-later-btn"
                        title="Save for later"
                        aria-label="Save for later"
                      >
                        💾
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Saved for Later Section */}
      {savedItems.length > 0 && (
        <div className="saved-for-later-section">
          <div className="saved-header">
            <h3>💾 Saved for Later ({savedItems.length} items)</h3>
            <p>Items you've saved to purchase later</p>
          </div>
          
          <div className="saved-content">
            {Object.values(groupedSavedItems).map(shopGroup => (
              <div key={`saved-${shopGroup.shop.id}`} className="saved-shop-section">
                <div className="saved-shop-header">
                  <h4>
                    {shopGroup.shop.type === 'medical' ? '🏥' : 
                     shopGroup.shop.type === 'bakery' ? '🥖' : 
                     shopGroup.shop.type === 'restaurant' ? '🍛' : '🏪'} {shopGroup.shop.name}
                  </h4>
                </div>

                <div className="saved-items">
                  {shopGroup.items.map(item => (
                    <div key={`saved-${item.product.id}-${item.shop.id}`} className="saved-item">
                      <div className="saved-item-image">
                        <img 
                          src={getValidImageUrl(item.product.image)}
                          alt={item.product.name}
                          onError={(e) => {
                            console.log('🌐 CrossShopCart - Saved item image failed to load:', item.product.image);
                            e.currentTarget.src = 'https://via.placeholder.com/60x60/667eea/ffffff?text=No+Image';
                          }}
                          onLoad={() => console.log('🌐 CrossShopCart - Saved item image loaded successfully:', item.product.image)}
                        />
                      </div>
                      
                      <div className="saved-item-details">
                        <h5>{item.product.name}</h5>
                        <p className="saved-item-brand">{item.product.brand}</p>
                        <div className="saved-item-price">
                          ${item.product.price.toFixed(2)} each
                        </div>
                        <div className="saved-item-quantity">
                          Qty: {item.quantity}
                        </div>
                      </div>

                      <div className="saved-item-actions">
                        {onMoveToCart && (
                          <button
                            onClick={() => onMoveToCart(item.product.id, item.shop.id)}
                            className="move-to-cart-btn"
                            title="Move to cart"
                          >
                            🛒 Move to Cart
                          </button>
                        )}
                        {onRemoveFromSaved && (
                          <button
                            onClick={() => onRemoveFromSaved(item.product.id, item.shop.id)}
                            className="remove-from-saved-btn"
                            title="Remove from saved"
                          >
                            🗑️
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="cart-footer">
        <div className="cart-totals">
          <div className="total-line">
            <span>Total Items:</span>
            <span>{getTotalItems()}</span>
          </div>
          <div className="total-line">
            <span>Number of Shops:</span>
            <span>{Object.keys(groupedItems).length}</span>
          </div>
          <div className="total-line grand-total-line">
            <span>Grand Total:</span>
            <span>${calculateGrandTotal().toFixed(2)}</span>
          </div>
        </div>

        <div className="checkout-section">
          <div className="checkout-note">
            <p>💡 <strong>Note:</strong> You'll be redirected to separate checkout pages for each shop.</p>
          </div>
          
          <div className="checkout-actions">
            {onContinueShopping && (
              <button
                onClick={onContinueShopping}
                className="continue-shopping-btn"
              >
                🛍️ Add More Items
              </button>
            )}
            
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log('Checkout button clicked, items:', items);
                if (items.length === 0) {
                  console.warn('Cannot checkout: cart is empty');
                  return;
                }
                const outOfStockItems = items.filter(item => !item.product.inStock);
                if (outOfStockItems.length > 0) {
                  console.warn('Cannot checkout: some items are out of stock', outOfStockItems);
                  alert(`Cannot proceed to checkout. ${outOfStockItems.length} item(s) are out of stock. Please remove them or wait for restock.`);
                  return;
                }
                handleCheckout();
              }}
              className="checkout-btn"
              disabled={items.length === 0 || items.some(item => !item.product.inStock)}
              style={{ 
                opacity: (items.length === 0 || items.some(item => !item.product.inStock)) ? 0.5 : 1,
                cursor: (items.length === 0 || items.some(item => !item.product.inStock)) ? 'not-allowed' : 'pointer'
              }}
            >
              {items.length === 0 
                ? 'Cart is empty' 
                : items.some(item => !item.product.inStock) 
                  ? 'Some items out of stock' 
                  : 'Proceed to Checkout'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CrossShopCart;
